import {
  InfoBox,
  InputSelect,
  InputText,
  Loading,
  useBreakpoint,
} from '@farmshare/ui-components';
import { RecursivePartial } from '@farmshare/utils';
import { type FormikProps } from 'formik';
import { useEffect, useRef } from 'react';
import { Col, Row } from 'react-bootstrap';

import {
  Cutsheet,
  EnumProcessorSchedulingAnimalSpecies,
  type ProcessorCapability,
  ProcessorSettings,
  usePrimalCutBlockerManyQuery,
  ViewSchedulingCreateQuery,
} from 'lib/graphql';

import {
  CutsheetCutsSection,
  CutsheetExtrasSection,
  CutsheetFormData,
  CutsheetSpecificationsSection,
  CutsheetTrimSection,
  useCutsheetInspectionLevelOptions,
} from 'pages/processor/cutsheets/_views/form-sections';

import { CustomizeCutsheetButton } from './customize-cutsheet-button';
import { useUserState } from 'providers/user-state-provider';

interface SchedulingCustomCutsheetModalProps {
  fP: FormikProps<CutsheetFormData>;
  capabilities?: RecursivePartial<ProcessorCapability>[];
  animalSpecies?: EnumProcessorSchedulingAnimalSpecies;
  settings?: RecursivePartial<ProcessorSettings>;
  isReadOnly?: boolean;
  canCustomizeCutsheet?: boolean;
  cutsheetId?: string;
  cutsheet?: Cutsheet;
  userCutsheets?: ViewSchedulingCreateQuery['cutsheetMany'];
  setUserCutsheets?: (
    newUserCutsheets: ViewSchedulingCreateQuery['cutsheetMany'],
  ) => void;
  buyerId?: string;
  producerId?: string;
  forcedType?: 'vendor' | 'user' | 'buyer' | 'producer';
  currentUserId?: string;
}

export function SchedulingCustomCutsheetModal({
  fP,
  capabilities,
  settings,
  animalSpecies,
  isReadOnly,
  canCustomizeCutsheet,
  cutsheetId,
  cutsheet,
  userCutsheets,
  setUserCutsheets,
  buyerId,
  producerId,
  forcedType,
  currentUserId,
}: SchedulingCustomCutsheetModalProps) {
  const breakpoint = useBreakpoint();
  const previousCutsheetTypeRef = useRef<string>();
  const { isProcessor } = useUserState();

  // Build cutsheet type options based on available props
  const cutsheetTypeOptions = [];
  if (!forcedType || (forcedType === 'vendor' && isProcessor)) {
    cutsheetTypeOptions.push({
      label: 'House Cutsheet',
      value: 'vendor',
    });
  }
  if (!forcedType || forcedType === 'user') {
    cutsheetTypeOptions.push({
      label: 'My Cutsheets',
      value: 'user',
    });
  }
  if (buyerId && (!forcedType || forcedType === 'buyer')) {
    cutsheetTypeOptions.push({
      label: 'Buyer Cutsheet',
      value: 'buyer',
    });
  }
  if (producerId && (!forcedType || forcedType === 'producer')) {
    cutsheetTypeOptions.push({
      label: 'Producer Cutsheet',
      value: 'producer',
    });
  }

  // Watch for cutsheetType changes and update type and userId accordingly
  const { cutsheetType } = fP.values as unknown as { cutsheetType?: string };

  useEffect(() => {
    // Only react to changes, not initial mount
    if (cutsheetType && cutsheetType !== previousCutsheetTypeRef.current) {
      previousCutsheetTypeRef.current = cutsheetType;

      switch (cutsheetType) {
        case 'vendor':
          fP.setFieldValue('type', 'vendor');
          fP.setFieldValue('userId', undefined);
          break;
        case 'user':
          fP.setFieldValue('type', 'user');
          fP.setFieldValue('userId', currentUserId);
          break;
        case 'buyer':
          fP.setFieldValue('type', 'user');
          fP.setFieldValue('userId', buyerId);
          break;
        case 'producer':
          fP.setFieldValue('type', 'user');
          fP.setFieldValue('userId', producerId);
          break;
        default:
          break;
      }
    }
  }, [cutsheetType, buyerId, producerId, currentUserId, fP]);

  const { data, loading } = usePrimalCutBlockerManyQuery({
    variables: {
      filter: {
        vendor: settings?.vendor?._id,
      },
      limit: 1000,
    },
  });

  const inspectionLevelOptions = useCutsheetInspectionLevelOptions(
    fP,
    settings as ProcessorSettings,
  );

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <div className="fs-5 fw-bold mb-2">Details</div>
      <Row className="g-3">
        <Col xs={12} lg={4}>
          <InputText
            label="Name"
            type="text"
            hint="Make this name something that is recognizable to you."
            floatingLabel
            required
            disabled={isReadOnly}
          />
        </Col>

        <Col xs={12} lg={4}>
          <InputSelect
            label="Inspection Level"
            nameOverride="inspectionLevel"
            options={inspectionLevelOptions}
            floatingLabel
            required
            disabled={isReadOnly}
          />
        </Col>
        {!isReadOnly && cutsheetId && (
          <Col xs={12} lg={2}>
            <InputSelect
              label="Do you want to replace existing cutsheet or save as a copy?"
              nameOverride="replaceExistingCutsheet"
              options={[
                { label: 'Replace Existing', value: 'replace' },
                { label: 'Save as Copy', value: 'copy' },
              ]}
              autoSelectFirstOption
              floatingLabel
              required
            />
          </Col>
        )}
        {!isReadOnly && !forcedType && cutsheetTypeOptions.length > 1 && (
          <Col xs={12} lg={4}>
            <InputSelect
              label="Cutsheet Type"
              nameOverride="cutsheetType"
              options={cutsheetTypeOptions}
              hint="Choose where to save this cutsheet"
              autoSelectFirstOption
              floatingLabel
              required
            />
          </Col>
        )}
        <Col xs={0} lg={2} />
        {canCustomizeCutsheet && setUserCutsheets && userCutsheets && (
          <Col xs={12} lg={2}>
            {['xs', 'sm', 'md'].includes(breakpoint) ? (
              <CustomizeCutsheetButton
                fP={fP}
                cutsheetId={cutsheetId}
                cutsheet={cutsheet}
                settings={settings}
                capabilities={capabilities}
                animalSpecies={animalSpecies}
                setUserCutsheets={setUserCutsheets}
                userCutsheets={userCutsheets}
              />
            ) : (
              <div className="d-flex justify-content-end">
                <CustomizeCutsheetButton
                  fP={fP}
                  cutsheetId={cutsheetId}
                  cutsheet={cutsheet}
                  settings={settings}
                  capabilities={capabilities}
                  animalSpecies={animalSpecies}
                  setUserCutsheets={setUserCutsheets}
                  userCutsheets={userCutsheets}
                />
              </div>
            )}
          </Col>
        )}
      </Row>
      <hr />
      <CutsheetSpecificationsSection
        fP={fP}
        capabilities={capabilities ?? []}
        readOnly={isReadOnly}
      />
      <hr />
      <div className="fs-5 fw-bold mb-2">Cuts</div>
      {!isReadOnly &&
        fP.values.inspectionLevel &&
        (fP.values.inspectionLevel as string) !== 'Select one...' && (
          <InfoBox
            content={
              <>
                Select all cuts that you would like. Every unselected cut will
                default to ground meat.
              </>
            }
          />
        )}

      <CutsheetCutsSection
        fP={fP}
        capabilities={capabilities ?? []}
        primalCutBlockers={data?.primalCutBlockerMany ?? []}
        readOnly={isReadOnly}
      />

      <hr />
      <Row xs={1} lg={2} className="g-2">
        <Col>
          <CutsheetExtrasSection
            fP={fP}
            capabilities={capabilities ?? []}
            readOnly={isReadOnly}
          />
        </Col>
        <Col>
          <CutsheetTrimSection
            fP={fP}
            capabilities={capabilities ?? []}
            readOnly={isReadOnly}
          />
        </Col>
      </Row>
    </>
  );
}
